import { useEffect, useMemo, useState } from "react";
import { LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P1RawDataPoint } from "../../../services/api/ChartDataFetch";
import {
  CHART_DIMENSION_PALETTE,
  CHART_SISTEMA_CAT,
  CHART_SISTEMA_ES,
  CHART_SISTEMA_EU,
  chartDimensionColorAt,
} from "../../../constants/chartColors";

export type Sistema = "CAT" | "ES" | "EU";

export interface P1FilterValues {
  yearRange?: [number, number];
  matriculacionsSistema: Sistema;
  concentracioSistema: Sistema;
  resumSistema: Sistema;
  ambit: string;
}

export interface P1ChartPoint {
  curs_academic: string;
  [key: string]: string | number;
}

const AMBIT_COLORS: Record<string, string> = {
  "Arts i humanitats":              CHART_DIMENSION_PALETTE[3],
  "Ciències":                       CHART_DIMENSION_PALETTE[0],
  "Ciències de la salut":           CHART_DIMENSION_PALETTE[4],
  "Ciències socials i jurídiques":  CHART_DIMENSION_PALETTE[1],
  "Enginyeria i arquitectura":      CHART_DIMENSION_PALETTE[2],
};

const SISTEMA_COLORS: Partial<Record<Sistema, string>> = {
  CAT: CHART_SISTEMA_CAT,
  ES:  CHART_SISTEMA_ES,
  EU:  CHART_SISTEMA_EU,
};

const SISTEMA_LABELS: Partial<Record<Sistema, string>> = {
  CAT: "Catalunya",
  ES:  "Espanya",
  EU:  "Europa",
};

function pivotByAmbit(filtered: P1RawDataPoint[]): P1ChartPoint[] {
  const cursosOrdenats = Array.from(
    new Set(filtered.map((d) => d.curs_academic))
  ).sort();

  return cursosOrdenats.map((curs) => {
    const point: P1ChartPoint = { curs_academic: curs };
    filtered
      .filter((d) => d.curs_academic === curs)
      .forEach((d) => { point[d.ambit_estudi] = d.ratio; });
    return point;
  });
}

function buildAmbitLines(filtered: P1RawDataPoint[]): LineConfig[] {
  const ambits = Array.from(
    new Set(filtered.map((d) => d.ambit_estudi))
  ).sort();

  return ambits.map((ambit, i) => ({
    key:   ambit,
    label: ambit,
    color: AMBIT_COLORS[ambit] ?? chartDimensionColorAt(i),
  }));
}

function pivotBySistema(filtered: P1RawDataPoint[]): P1ChartPoint[] {
  const cursosOrdenats = Array.from(
    new Set(filtered.map((d) => d.curs_academic))
  ).sort();

  return cursosOrdenats.map((curs) => {
    const point: P1ChartPoint = { curs_academic: curs };
    filtered
      .filter((d) => d.curs_academic === curs)
      .forEach((d) => {
        const label = SISTEMA_LABELS[d.sistema as Sistema] ?? d.sistema;
        point[label] = d.ratio;
      });
    return point;
  });
}

function buildSistemaLines(filtered: P1RawDataPoint[]): LineConfig[] {
  const sistemes = Array.from(
    new Set(filtered.map((d) => d.sistema as Sistema))
  ).sort() as Sistema[];

  return sistemes.map((s, i) => ({
    key:   SISTEMA_LABELS[s] ?? s,
    label: SISTEMA_LABELS[s] ?? s,
    color: SISTEMA_COLORS[s] ?? chartDimensionColorAt(i),
  }));
}

export function useP1Data(filters: P1FilterValues) {
  const [rawData, setRawData] = useState<P1RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P1RawDataPoint[]>("p1") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P1RawDataPoint[]>("p1") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p1") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP1Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableAmbits = useMemo(() => {
    return Array.from(new Set(rawData.map((d) => d.ambit_estudi))).sort();
  }, [rawData]);

  const yearFiltered = useMemo(() => {
    const range = filters.yearRange;
    if (!range) return rawData;
    return rawData.filter((d) => {
      const year = parseInt(d.curs_academic.split("/")[0], 10);
      return year >= range[0] && year <= range[1];
    });
  }, [rawData, filters.yearRange]);

  const { chart1Data, chart1Lines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.sistema === filters.matriculacionsSistema);
    return {
      chart1Data: pivotByAmbit(filtered),
      chart1Lines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.matriculacionsSistema]);

  const { chart1HeatmapData, chart1HeatmapLines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.sistema === filters.concentracioSistema);
    return {
      chart1HeatmapData: pivotByAmbit(filtered),
      chart1HeatmapLines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.concentracioSistema]);

  const { chart1SummaryData, chart1SummaryLines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.sistema === filters.resumSistema);
    return {
      chart1SummaryData: pivotByAmbit(filtered),
      chart1SummaryLines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.resumSistema]);

  const { chart2Data, chart2Lines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.ambit_estudi === filters.ambit);
    return {
      chart2Data: pivotBySistema(filtered),
      chart2Lines: buildSistemaLines(filtered),
    };
  }, [yearFiltered, filters.ambit]);

  return {
    chart1Data,
    chart1Lines,
    chart1HeatmapData,
    chart1HeatmapLines,
    chart1SummaryData,
    chart1SummaryLines,
    chart2Data,
    chart2Lines,
    availableAmbits,
    loading,
    error,
  };
}
